// Eastern Time helpers for ADMIN time-entry edit/create flows.
//
// The DB stores UTC. The company operates in Eastern Time, so a time an admin
// types ("07:00") must always mean 07:00 *Eastern*, regardless of the timezone
// of the admin's computer. These helpers convert between UTC ISO strings and an
// Eastern wall clock WITHOUT ever reading the viewer's local timezone.
//
// easternOffsetMinutes() matches the sign convention of Date.getTimezoneOffset()
// (minutes to ADD to local to reach UTC): 240 during EDT, 300 during EST. The
// backend create/bulk-adjust endpoints expect that same convention via
// `timezone_offset_minutes`, so passing the Eastern offset makes them interpret
// the typed time as Eastern instead of as the viewer's local time.

const ET = 'America/New_York';

/** Minutes to ADD to an Eastern wall-clock time to get UTC (240 EDT / 300 EST). */
export function easternOffsetMinutes(ref: Date = new Date()): number {
  const s = ref.toLocaleString('en-US', { timeZone: ET, timeZoneName: 'short' });
  return s.includes('EDT') ? 240 : 300;
}

/**
 * A Date whose LOCAL fields (getDay/getDate/getMonth/getFullYear) equal the
 * current Eastern wall clock — for day-of-week / week-window math that must be
 * anchored to the Eastern calendar, not the viewer's. NOT a real instant; use
 * only for calendar math, never store its toISOString() as a clock time.
 */
export function easternNow(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: ET }));
}

/** 'YYYY-MM-DD' + 'HH:mm' interpreted as an Eastern wall clock -> UTC Date. */
export function easternWallClockToUTC(dateStr: string, timeStr: string): Date {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  const wallAsUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
  // Offset is determined by the calendar date (noon avoids the 2 AM DST switch).
  const off = easternOffsetMinutes(new Date(`${dateStr}T12:00:00Z`));
  return new Date(wallAsUTC + off * 60 * 1000);
}

/** UTC ISO -> 'YYYY-MM-DD' on the Eastern calendar (for <input type="date">). */
export function isoToEasternDate(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: ET });
}

/** UTC ISO -> 'HH:mm' (24h) Eastern wall clock (for <input type="time">). */
export function isoToEasternTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: ET,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// Clock-out resolution against a clock-in — shared by every edit screen.
// (Mirrors bfav5's src/utils/easternTime.ts; keep the two in sync.)
//
// Payroll rounds punches to the nearest 15 minutes, so an entered clock-out a
// few RAW minutes before the entry's clock-in usually lands on the SAME
// quarter, not the next day (entered 1:08 rounds to 1:15 == the 1:15 clock-in
// of an entry opened at 1:09 — a valid 0-hour quick-switch entry). Compare
// ROUNDED values; only a rounded time strictly before the clock-in means
// "next day", and that must be confirmed by the user, never silently assumed.
// The backend enforces the same rule and rejects shifts over 16 hours.
// ---------------------------------------------------------------------------

const QUARTER_MS = 15 * 60 * 1000;

/** Round a Date to the nearest 15 min ("round half up"), matching backend payroll rounding. */
export function roundToNearestQuarter(d: Date): Date {
  return new Date(Math.round(d.getTime() / QUARTER_MS) * QUARTER_MS);
}

export interface ResolvedClockOut {
  /** The datetime to save (same day, or +24h when nextDay is true). */
  date: Date;
  /** True when the entered time only makes sense on the following day — confirm with the user before saving. */
  nextDay: boolean;
}

/**
 * Resolve an entered clock-out (already converted to a UTC Date on the
 * clock-in's calendar day) against the entry's clock-in.
 */
export function resolveClockOut(clockInIso: string, sameDayClockOut: Date): ResolvedClockOut {
  const clockInMs = roundToNearestQuarter(new Date(clockInIso)).getTime();
  const roundedOutMs = roundToNearestQuarter(sameDayClockOut).getTime();
  if (roundedOutMs >= clockInMs) return { date: sameDayClockOut, nextDay: false };
  return { date: new Date(sameDayClockOut.getTime() + 24 * 60 * 60 * 1000), nextDay: true };
}

/** Standard confirmation prompt for a next-day (overnight) clock-out. */
export function nextDayConfirmMessage(enteredTime: string, clockInIso: string): string {
  const inTime = new Date(clockInIso).toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', hour12: true });
  const inDate = new Date(clockInIso).toLocaleDateString('en-US', { timeZone: ET, month: 'short', day: 'numeric' });
  return `${enteredTime} is before this entry's clock-in (${inTime} on ${inDate}).\n\nSave it as the NEXT DAY (overnight shift)?\n\nOK = save as next day\nCancel = go back and fix the time`;
}
